import { type ComponentProps, useCallback, useMemo, useState } from "react";

import type { TicketType } from "@features/ticket-card/model";

import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { Popup } from "@shared/ui/popup";
import { Body, Heading } from "@shared/ui/typography";

import { PopupContent } from "../popup-content";

import s from "./details.module.scss";

export type DetailsProps = ComponentProps<"div"> & {
  data: TicketType;
  onBuyClick?: () => void;
};

export const Details = (props: DetailsProps) => {
  const {
    data: { description, moreDetails, isNew, image, documentId, title, price },
    onBuyClick,
  } = props;

  const [isOpenPopup, setOpenPopup] = useState(false);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
  }, []);

  const titleHtml = useMemo(() => {
    return title?.replace(/\n/g, "<br />") ?? "";
  }, [title]);

  /**
   * Процент скидки показываем, только если он похож на процент: в админке
   * Strapi числовой инпут переформатирует значение на каждый символ, и при
   * правке в середине строки легко получить «3 243242» вместо «40».
   * Мусорное значение не должно уезжать на витрину бейджем «-3243242%»
   */
  const discountPercent = useMemo(() => {
    const value = price?.adult?.discountPercent;

    if (typeof value !== "number" || !Number.isFinite(value)) {
      return null;
    }

    return value > 0 && value < 100 ? Math.round(value) : null;
  }, [price?.adult?.discountPercent]);

  const hasDetails = useMemo(() => {
    return (
      moreDetails &&
      moreDetails.length > 0 &&
      moreDetails
        ?.map((item) =>
          item?.children.filter((child) => child?.type !== "list-item"),
        )
        .filter((child) => (child?.length ?? 0) > 0)
        .flat(1)
        .filter((child) => (child?.text?.length ?? 0) > 0).length > 0
    );
  }, [moreDetails]);

  // Используем максимальный процент скидки из взрослого и детского билетов
  // const discountPercent = useMemo(() => {
  //   if (!price) {
  //     return null;
  //   }

  //   const adultPrice = price.adult;
  //   const childrenPrice = price.childen;

  //   const adultDiscount =
  //     adultPrice && typeof adultPrice === "object"
  //       ? (adultPrice.discountPercent ?? null)
  //       : null;

  //   const childrenDiscount =
  //     childrenPrice && typeof childrenPrice === "object"
  //       ? (childrenPrice.discountPercent ?? null)
  //       : null;

  //   // Возвращаем максимальный процент скидки или null, если скидки нет
  //   if (adultDiscount === null && childrenDiscount === null) {
  //     return null;
  //   }
  //   if (adultDiscount === null) {
  //     return childrenDiscount !== null
  //       ? Math.round(childrenDiscount / 5) * 5
  //       : null;
  //   }
  //   if (childrenDiscount === null) {
  //     return Math.round(adultDiscount / 5) * 5;
  //   }

  //   const maxDiscount = Math.max(adultDiscount, childrenDiscount);

  //   return Math.round(maxDiscount / 5) * 5;
  // }, [price]);

  return (
    <>
      <div className={s.root}>
        <div className={s.tags}>
          {isNew && (
            <div className={s.newTag}>
              <Body size="special" weight="semiBold">
                New
              </Body>
            </div>
          )}
          {discountPercent !== null && (
            <div className={s.saleTag}>
              <Body size="special" weight="semiBold">
                {`-${discountPercent}%`}
              </Body>
            </div>
          )}
        </div>
        {image?.url && (
          <Image
            className={s.image}
            src={image.url}
            alt={image?.alternativeText ?? "ticket image"}
            height="100%"
          />
        )}
        <div className={s.wrapper}>
          <div className={s.content}>
            <Heading
              level={5}
              tag="h2"
              className={s.title}
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />
            <div className={s.bottom}>
              {description && <Body size="caption">{description}</Body>}
              {hasDetails && (
                <Button
                  className={s.moreBtn}
                  onClick={() => setOpenPopup(true)}
                >
                  <Icon size={16} name="math-plus" className={s.plusIcon} />
                  <Body weight="semiBold" size="uppercase-small">
                    Важно знать
                  </Body>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {hasDetails && (
        <Popup
          id={`ticket-details-${documentId}`}
          onClose={handleClosePopup}
          isOpen={isOpenPopup}
          variant="ticket"
        >
          <PopupContent
            onBuyClick={onBuyClick}
            onClose={handleClosePopup}
            data={props.data}
          />
        </Popup>
      )}
    </>
  );
};

Details.displayName = "Details";
